import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowDown, CheckCircle2, GitBranch, Loader2, RefreshCw, Server } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';

type Image = { repository: string; digest: string | null; source?: string | null };

type Stage = {
  kind: 'ci' | 'aoc' | 'truncated';
  name: string;
  here: boolean;
  reachable: boolean;
  version: string | null;
  published_at?: string | null;
  available?: string | null;
  in_sync?: boolean;
  error?: string;
  images?: Record<string, Image>;
  clis?: { aoc_cli?: string | null; bitswan_cli?: string | null };
};

// The order the four are shown in, and what to call them.
const COMPONENTS: Array<[string, string]> = [
  ['backend', 'backend'],
  ['frontend', 'AOC'],
  ['global_admin', 'global admin'],
  ['keycloak', 'keycloak'],
];

function short(digest?: string | null) {
  if (!digest) return '—';
  return digest.startsWith('sha256:') ? digest.slice(7, 19) : digest.slice(0, 12);
}

function WhatIsHere({ stage }: { stage: Stage }) {
  const images = stage.images ?? {};
  const clis = stage.clis ?? {};
  if (Object.keys(images).length === 0 && !clis.aoc_cli && !clis.bitswan_cli) return null;
  return (
    <div className="mt-3 grid gap-x-8 gap-y-1 border-t pt-3 text-xs sm:grid-cols-2">
      {COMPONENTS.map(([key, label]) => (
        <div key={key} className="flex items-baseline justify-between gap-3">
          <span className="text-muted-foreground">{label}</span>
          <code
            className="font-mono text-[11px]"
            title={images[key]?.source || images[key]?.digest || ''}
            data-testid={`stage-image-${stage.name}-${key}`}
          >
            {short(images[key]?.digest)}
          </code>
        </div>
      ))}
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-muted-foreground">on-prem AOC CLI</span>
        <code className="font-mono text-[11px]" data-testid={`stage-aoc-cli-${stage.name}`}>
          {clis.aoc_cli ?? '—'}
        </code>
      </div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-muted-foreground">bitswan CLI</span>
        <code className="font-mono text-[11px]" data-testid={`stage-bitswan-cli-${stage.name}`}>
          {clis.bitswan_cli ?? '—'}
        </code>
      </div>
    </div>
  );
}

const QK = ['supply-chain'];

function when(value?: string | null) {
  if (!value) return null;
  return new Date(value).toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function StageRow({ stage, behind }: { stage: Stage; behind: string | null }) {
  const Icon = stage.kind === 'ci' ? GitBranch : Server;
  return (
    <div
      className={`rounded-md border p-4 ${stage.here ? 'border-primary/40 bg-primary/5' : ''}`}
      data-testid={`supply-chain-stage-${stage.name}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Icon className="mt-0.5 h-4 w-4 text-muted-foreground" />
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{stage.name}</span>
              {stage.here && <Badge variant="secondary">this AOC</Badge>}
              {stage.kind === 'ci' && (
                <span className="text-xs text-muted-foreground">where builds come from</span>
              )}
            </div>
            {stage.reachable ? (
              <p className="mt-1 text-xs text-muted-foreground">
                {when(stage.published_at) ? `published ${when(stage.published_at)}` : 'no release yet'}
              </p>
            ) : (
              <p className="mt-1 text-xs text-amber-700">
                could not be reached{stage.error ? ` — ${stage.error}` : ''}
              </p>
            )}
          </div>
        </div>

        <div className="text-right">
          {stage.version ? (
            <code className="rounded bg-muted px-2 py-1 text-sm">{stage.version}</code>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          )}
          {behind && (
            <p className="mt-1 text-xs text-amber-700" data-testid={`supply-chain-behind-${stage.name}`}>
              {behind} is further ahead
            </p>
          )}
          {stage.here && stage.available && stage.available !== stage.version && (
            <p className="mt-1 text-xs text-amber-700">{stage.available} is published here but not running</p>
          )}
          {stage.in_sync === false && (
            <p className="mt-1 text-xs text-amber-700">its containers are not on that release</p>
          )}
        </div>
      </div>
      <WhatIsHere stage={stage} />
    </div>
  );
}

export default function SupplyChainPage() {
  const queryClient = useQueryClient();

  const chain = useQuery({
    queryKey: QK,
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.get('/global-admin/supply-chain/')).data.stages as Stage[];
    },
  });

  const stages = chain.data ?? [];
  // Nearest first from the API. Shown the other way up, so a release travels
  // down the page the way it travels down the chain.
  const shown = [...stages].reverse();

  return (
    <div className="space-y-6" data-testid="supply-chain-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Supply chain</h1>
          <p className="text-sm text-muted-foreground">
            Everything a build passes through before it reaches this AOC: the release each stage
            is on, the image behind each of its four parts, and the two command-line tools it
            serves. Each stage knows only the one above it and asks it over the API in turn, so
            this is the whole line without anyone holding a list of it.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={async () => {
            const api = await authenticatedBitswanBackendInstance();
            await api.get('/global-admin/supply-chain/?refresh=1');
            queryClient.invalidateQueries({ queryKey: QK });
          }}
          data-testid="supply-chain-refresh"
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Ask again
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>From the build to here</CardTitle>
          <CardDescription>
            A stage that cannot be reached is shown rather than left out, because a gap in the
            chain is the thing worth seeing.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {chain.isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : shown.length === 0 ? (
            <p className="py-6 text-center text-muted-foreground">Nothing to show yet.</p>
          ) : (
            <div className="space-y-0" data-testid="supply-chain-list">
              {shown.map((stage, index) => {
                const next = shown[index + 1];
                // Only three things can be said about a pair, and claiming the
                // wrong one is worse than saying nothing: they match, they
                // differ, or one of them could not be asked.
                const comparable = Boolean(next && stage.version && next.version);
                const aheadOfNext = comparable && stage.version !== next.version;
                return (
                  <div key={`${stage.name}-${index}`}>
                    <StageRow stage={stage} behind={null} />
                    {next && (
                      <div className="flex items-center gap-2 py-2 pl-6 text-xs text-muted-foreground">
                        <ArrowDown className="h-3.5 w-3.5" />
                        {!comparable ? (
                          <span>nothing to compare</span>
                        ) : aheadOfNext ? (
                          <span className="flex items-center gap-1 text-amber-700">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            {next.name} has not taken {stage.version} yet
                          </span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            in step
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
